import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAppTranslation } from "../i18n/useAppTranslation";
import { SupportedLanguage } from "../i18n";
import { setSettings } from "../redux/settings/action";
import { LANGUAGE_SWITCHING } from "../config";

type LanguageSwitcherProps = {
  className?: string;
  compact?: boolean;
  // Leaves the "Language" caption out of the full-size button, where the place
  // around it already says so.
  hideLabel?: boolean;
  // The list opens under the button's end edge, or centred under it.
  menuAlign?: "end" | "center";
};

export const LANGUAGE_OPTIONS: Array<{
  code: SupportedLanguage;
  labelKey: string;
  nativeLabel: string;
  flag: string;
}> = [
  { code: "en", labelKey: "language.english", nativeLabel: "English", flag: "🇬🇧" },
  { code: "es", labelKey: "language.spanish", nativeLabel: "Español", flag: "🇪🇸" },
  { code: "pt", labelKey: "language.portuguese", nativeLabel: "Português", flag: "🇵🇹" },
  { code: "ja", labelKey: "language.japanese", nativeLabel: "日本語", flag: "🇯🇵" },
  { code: "zh", labelKey: "language.chinese", nativeLabel: "中文", flag: "🇨🇳" },
  { code: "ru", labelKey: "language.russian", nativeLabel: "Русский", flag: "🇷🇺" },
  { code: "uk", labelKey: "language.ukrainian", nativeLabel: "Українська", flag: "🇺🇦" },
  { code: "it", labelKey: "language.italian", nativeLabel: "Italiano", flag: "🇮🇹" },
  { code: "de", labelKey: "language.german", nativeLabel: "Deutsch", flag: "🇩🇪" },
  { code: "fa", labelKey: "language.persian", nativeLabel: "فارسی", flag: "🇮🇷" },
  { code: "ar", labelKey: "language.arabic", nativeLabel: "العربية", flag: "🇸🇦" },
];

// Height of one row of the list, and the most the list takes before it scrolls.
const OPTION_HEIGHT = 32;
const MENU_MAX_HEIGHT = 380;

export const GlobeIcon = ({ size = 18 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <circle cx="12" cy="12" r="9.5" />
    <ellipse cx="12" cy="12" rx="4.2" ry="9.5" />
    <path d="M2.5 12h19M4 7h16M4 17h16" />
  </svg>
);

// The current language's option, and the one way to switch: the app's language
// and the saved setting together. Every picker uses it.
export const useLanguageChoice = () => {
  const dispatch = useDispatch();
  const userSettings = useSelector((state: any) => state.userSettings || {});
  const { currentLanguage, changeLanguage } = useAppTranslation("common");
  const currentOption =
    LANGUAGE_OPTIONS.find((option) => option.code === currentLanguage) ||
    LANGUAGE_OPTIONS[0];
  const chooseLanguage = async (nextLanguage: SupportedLanguage) => {
    if (nextLanguage === currentOption.code) return;
    await changeLanguage(nextLanguage);
    dispatch(
      setSettings({
        ...userSettings,
        language: nextLanguage,
      })
    );
  };
  return { currentOption, chooseLanguage };
};

const LanguageMenu: React.FC<LanguageSwitcherProps> = ({
  className = "",
  compact = false,
  hideLabel = false,
  menuAlign = "end",
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  // Opens upwards when there is no room under the button (the last row of a
  // dialog, the bottom of a phone screen).
  const [menuPlacement, setMenuPlacement] = React.useState<{
    up: boolean;
    maxHeight: number;
  }>({ up: false, maxHeight: MENU_MAX_HEIGHT });
  const { t } = useAppTranslation("common");
  const { currentOption, chooseLanguage } = useLanguageChoice();

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const toggleMenu = () => {
    if (!isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - 12;
      const spaceAbove = rect.top - 12;
      const needed = Math.min(
        MENU_MAX_HEIGHT,
        LANGUAGE_OPTIONS.length * OPTION_HEIGHT + 12
      );
      const up = spaceBelow < needed && spaceAbove > spaceBelow;
      setMenuPlacement({
        up,
        maxHeight: Math.max(
          4 * OPTION_HEIGHT,
          Math.min(MENU_MAX_HEIGHT, up ? spaceAbove : spaceBelow)
        ),
      });
    }
    setIsOpen((prev) => !prev);
  };

  const handleLanguageSelect = async (nextLanguage: SupportedLanguage) => {
    await chooseLanguage(nextLanguage);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={`position-relative ${className}`}
      style={{ width: "fit-content" }}
    >
      <button
        type="button"
        className="polygon-btn yellow d-flex align-items-center justify-content-center"
        style={{
          minWidth: compact ? 92 : hideLabel ? 130 : 168,
          height: compact ? 34 : 40,
          gap: 6,
        }}
        onClick={toggleMenu}
        title={t("language.switchTo")}
        aria-label={t("language.switchTo")}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {!compact && !hideLabel && (
          <span className="text-light fs-small">{t("language.label")}</span>
        )}
        {/* Direct children only: .polygon-btn un-skews each child of the
            skewed button, so a nested one would lean the other way. */}
        <span aria-hidden="true">{currentOption.flag}</span>
        <span className="text-light fw-bold" lang={currentOption.code}>
          {compact ? currentOption.code.toUpperCase() : currentOption.nativeLabel}
        </span>
        <span
          className="text-light"
          aria-hidden="true"
          style={{
            transform: isOpen ? "skew(20deg) rotate(180deg)" : undefined,
            transition: "transform 0.2s ease",
          }}
        >
          ▾
        </span>
      </button>

      {isOpen && (
        <div
          role="listbox"
          aria-label={t("language.label")}
          className="p-1"
          style={{
            position: "absolute",
            ...(menuPlacement.up
              ? { bottom: "calc(100% + 6px)" }
              : { top: "calc(100% + 6px)" }),
            ...(menuAlign === "center"
              ? { left: "50%", transform: "translateX(-50%)" }
              : { insetInlineEnd: 0 }),
            minWidth: compact ? 200 : 230,
            maxHeight: menuPlacement.maxHeight,
            overflowY: "auto",
            background: "rgba(22, 19, 14, 0.98)",
            border: "1px solid #a37e39",
            borderRadius: 8,
            zIndex: 1200,
            boxShadow: "0 6px 18px rgba(0, 0, 0, 0.35)",
          }}
        >
          {LANGUAGE_OPTIONS.map((option) => {
            const selected = currentOption.code === option.code;
            // The language's own name, so a player finds theirs whatever the
            // screen is in; the name in the current language follows it.
            const localName = t(option.labelKey, {
              defaultValue: option.nativeLabel,
            });
            return (
              <button
                key={option.code}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => handleLanguageSelect(option.code)}
                className="w-100 d-flex align-items-center justify-content-between gap-2 px-2 py-1 border-0"
                style={{
                  minHeight: OPTION_HEIGHT,
                  color: selected ? "#ffdb8c" : "#dcdcdc",
                  fontWeight: selected ? 700 : 500,
                  fontSize: 13,
                  borderRadius: 6,
                  textAlign: "start",
                  whiteSpace: "nowrap",
                  background: selected
                    ? "rgba(163, 126, 57, 0.22)"
                    : "transparent",
                }}
              >
                <span className="d-flex align-items-center gap-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center"
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: "rgba(255, 255, 255, 0.08)",
                      fontSize: 14,
                      flexShrink: 0,
                    }}
                    aria-hidden="true"
                  >
                    {option.flag}
                  </span>
                  <span lang={option.code}>{option.nativeLabel}</span>
                  {localName !== option.nativeLabel && (
                    <span
                      className="opacity-75"
                      style={{ fontSize: 11, fontWeight: 400 }}
                    >
                      {localName}
                    </span>
                  )}
                </span>
                <span
                  className="opacity-75"
                  style={{
                    minWidth: 40,
                    textAlign: "end",
                    fontSize: 11,
                  }}
                >
                  {selected ? "✓ " : ""}
                  {option.code.toUpperCase()}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// "Select language" and the switcher beside it, centred: the picker of the
// login screens. It stays above the blocks under it, so its list opens over them.
const LanguageRow = ({ className = "" }: { className?: string }) => {
  const { t } = useAppTranslation("auth");

  return (
    <div
      className={`d-flex flex-wrap align-items-center justify-content-center gap-2 ${className}`}
      style={{ position: "relative", zIndex: 5 }}
    >
      <span className="d-flex align-items-center gap-1 fs-small dark-yellow">
        <GlobeIcon />
        {t("auth:login.selectLanguage")}
      </span>
      <LanguageSwitcher hideLabel menuAlign="center" />
    </div>
  );
};

// Nothing where players cannot switch language (the play build; see LANGUAGE_SWITCHING).
const LanguageSwitcher: React.FC<LanguageSwitcherProps> = (props) =>
  LANGUAGE_SWITCHING ? <LanguageMenu {...props} /> : null;

export const LanguagePickerRow = (props: { className?: string }) =>
  LANGUAGE_SWITCHING ? <LanguageRow {...props} /> : null;

export default LanguageSwitcher;
